import { matchPath } from 'react-router-dom'
import routes from './routes'

/**
 * Route patterns unavailable on a mobile viewport.
 *
 * These are EXACT route patterns, matched with react-router's `matchPath` —
 * never string prefixes. A prefix rule such as `pathname.startsWith('/admin')`
 * is a bug that ships: it also swallows `/admin/userprofile` (the only mobile
 * path to Logout, reached from the header avatar), `/admin/trial-users` and
 * `/admin/support`, none of which appear in the Admin panel and none of which
 * this phase was asked to disable.
 *
 * The list covers three surfaces:
 *
 *  1. Evaluations. The sidebar group is only Approvals / Evaluation Sites /
 *     Reports, but `/compare`, `/scoring`, `/financials`, `/risks` and
 *     `/proscons` are routed evaluation workspaces with no nav entry. They are
 *     reachable only from inside an evaluation, which is itself gated — but a
 *     bookmark would otherwise walk straight into an ungated evaluation screen.
 *  2. Explorer. Both the bare `/explorer` redirect and `/explorer/:grain`. See
 *     the note below on why gating only one is a trap.
 *  3. The ADMIN panel sections: Library, Archives, Templates, Utilities, the
 *     admin index, and the `:toolType` catch-all behind them.
 *
 * Parent patterns are enough for nested routes. `/evaluations/:id` renders its
 * children through an `<Outlet/>`, so blocking the parent blocks all of them.
 */
export const DESKTOP_ONLY_ROUTES: string[] = [
	// --- Evaluations -------------------------------------------------------
	routes.EVALUATIONS,
	`${routes.EVALUATIONS}/:id/map`,
	// Parent plus splat. The route table only needs the parent — its <Outlet/>
	// children (sites, scoring, financials, compare, risks, proscons, approvals,
	// activity, reports) never mount if it does not. The oracle needs the `/*`
	// form too, because `matchPath` requires a FULL match otherwise and would
	// answer "not blocked" for /evaluations/:id/scoring.
	`${routes.EVALUATIONS}/:id`,
	`${routes.EVALUATIONS}/:id/*`,
	routes.COMPARE,
	routes.SCORING,
	routes.FINANCIALS,
	routes.RISKS,
	routes.PROSCONS,
	routes.APPROVALS,
	routes.REPORTS,
	routes.ALL_SITES,
	// Parent plus splat, same reasoning as the evaluation detail route above.
	`${routes.ALL_SITES}/:siteId`,
	`${routes.ALL_SITES}/:siteId/*`,

	// --- Explorer ----------------------------------------------------------
	// Both, deliberately. `/explorer` is a bare <Navigate> to `/explorer/locations`.
	// Gating only the grain route would let a mobile tap rewrite the URL first
	// and block second, so browser Back would redirect forward again — a loop
	// the user cannot escape.
	routes.EXPLORER,
	routes.EXPLORER_GRAIN,

	// --- Presentations (MYD-494) ---------------------------------------------
	// The gallery and the editor only. PRESENTATION_PRESENT is deliberately
	// absent: presenting has to work on a tablet or phone.
	routes.PRESENTATIONS,
	routes.PRESENTATION_EDIT,

	// --- ADMIN panel sections ----------------------------------------------
	routes.ADMIN,
	`${routes.ADMIN}/library/documents`,
	`${routes.ADMIN}/library/images`,
	`${routes.ADMIN}/archives/locations`,
	`${routes.ADMIN}/archives/portfolios`,
	`${routes.ADMIN}/archives/projects`,
	`${routes.ADMIN}/templates/tasks`,
	`${routes.ADMIN}/templates/sitedetails`,
	`${routes.ADMIN}/templates/scoring-criteria`,
	`${routes.ADMIN}/templates/acquisition`,
	`${routes.ADMIN}/templates/operating`,
	`${routes.ADMIN}/templates/roi-analysis`,
	`${routes.ADMIN}/templates/risks`,
	`${routes.ADMIN}/templates/pros-cons`,
	`${routes.ADMIN}/utilities/bulkload`,
	`${routes.ADMIN}/utilities/lookuplists`,
	`${routes.ADMIN}/utilities/cleanup`,
	`${routes.ADMIN}/utilities/ai-usage`,
	// The Admin_Show catch-all sitting behind the sections above. Static-segment
	// routes outrank it in react-router's ranking, so gating it cannot reach
	// `/admin/userprofile`, `/admin/trial-users` or `/admin/support`.
	`${routes.ADMIN}/:toolType/*`,
]

/**
 * Routes that must stay reachable on mobile even though a broader pattern in
 * `DESKTOP_ONLY_ROUTES` would otherwise match them.
 *
 * This models react-router's own ranking rather than duplicating a rule. In the
 * real router a static segment outranks a dynamic one, so `/admin/userprofile`
 * wins over `/admin/:toolType/*` and renders normally. `matchPath` used
 * standalone has no such ranking — it answers about ONE pattern — so the oracle
 * has to state the exception itself or it over-blocks.
 *
 * `/admin/userprofile` is load bearing: the header avatar dropdown is the only
 * route to User Profile and Logout once the sidebar's User section is hidden.
 * The other two are admin tooling that never appeared in the Admin panel and
 * were never in scope for this phase; support impersonation from a phone is a
 * legitimate ops need.
 */
export const MOBILE_ALLOWED_ROUTES: string[] = [
	`${routes.ADMIN}/userprofile`,
	routes.ADMIN_TRIAL_USERS,
	routes.ADMIN_SUPPORT,
	/*
	 * Help Docs, which `${routes.ADMIN}/:toolType/*` would otherwise swallow.
	 *
	 * Deliberately reachable on a phone. The document it publishes is a guide to
	 * setting up a phone, so blocking it there would be perverse: the likeliest
	 * way anyone arrives is a colleague sending the link to exactly the device
	 * it is about. The page stacks cleanly at 390px, verified on a phone
	 * viewport, so there is nothing to protect the reader from.
	 */
	routes.ADMIN_HELP_DOCS,
]

/**
 * Is this path one of the surfaces hidden on mobile?
 *
 * Used in two places, which is the point: the sidebar filters its nav items
 * through it, and the route guard blocks navigation with it. One list means a
 * surface can never be hidden in the nav but left open at the URL, or vice
 * versa — and a test can walk the rendered sidebar and assert no link it shows
 * on mobile leads somewhere blocked.
 */
export const isDesktopOnlyPath = (pathname: string): boolean => {
	const matches = (pattern: string) => matchPath(pattern, pathname) !== null
	// Allow wins, mirroring the router's static-over-dynamic ranking.
	if (MOBILE_ALLOWED_ROUTES.some(matches)) return false
	return DESKTOP_ONLY_ROUTES.some(matches)
}

export default DESKTOP_ONLY_ROUTES
